/**
 * useExemplarMember(section) — resolves the DEFAULT member for a picker-driven clinical screen so the
 * screen renders REAL data on first paint instead of defaulting to the alphabetically-first roster
 * member (who may have 0 rows and make the screen look broken). It dedups the ~6 copy-pasted
 * "default to the first roster member" effects into one hook.
 *
 * Resolution (honest, never fabricated):
 *   1. GET /participants/exemplar?section=… -> the member with the MOST rows for that section.
 *   2. If that returns null (the backing op_* table is absent/empty) -> fall back to the first roster
 *      member, exactly as the old per-screen effect did. The ParticipantPicker still lets the user
 *      change member; this only seeds the initial selection.
 *
 * enterprise_member_key is a non-PHI surrogate; nothing is logged here.
 */
import { useEffect, useState } from "react";
import type { Participant } from "@shared/contracts";
import { fetchEnvelope } from "./api";

export type ExemplarSection =
  | "conditions"
  | "vitals"
  | "encounters"
  | "care_plan"
  | "medications"
  | "allergies"
  | "diagnostic_reports";

interface ExemplarPayload {
  enterprise_member_key: string;
  n: number;
}

export function useExemplarMember(section: ExemplarSection): {
  key: string | null;
  loading: boolean;
  /** True when BOTH the exemplar lookup and the roster fallback failed, so no default member resolved.
   *  Picker screens surface an honest "could not load a default member - search above" bar instead of
   *  silently going blank. BACKWARD-COMPATIBLE add: existing consumers keep destructuring key/loading. */
  error: boolean;
} {
  const [key, setKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // AbortController: a section change (or unmount) aborts the in-flight request, fixing a latent race
    // (a slow prior section resolving after a newer one) and dropping the wasted network round-trip.
    const controller = new AbortController();
    setLoading(true);
    setKey(null);
    setError(false);
    fetchEnvelope<ExemplarPayload | null>(
      `/api/v1/participants/exemplar?section=${section}`,
      { signal: controller.signal },
    )
      .then(async ({ data }) => {
        if (cancelled) return;
        if (data?.enterprise_member_key) {
          setKey(data.enterprise_member_key);
          return;
        }
        // Honest fallback: the section's table is absent/empty -> seed the first roster member so the
        // screen still defaults to SOME real member (never a fabricated key).
        const { data: first } = await fetchEnvelope<Participant[]>(
          "/api/v1/participants?page=1&pageSize=1",
          { signal: controller.signal },
        );
        if (!cancelled) setKey(first[0]?.enterprise_member_key ?? null);
      })
      .catch(() => {
        // An abort is intentional (section change/unmount) — cancelled is already set, so we do not flag
        // it as an error. A genuine failure surfaces error=true so the picker screen can offer a way out;
        // the picker still allows manual selection. No PHI logged.
        if (cancelled) return;
        setKey(null);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [section]);

  return { key, loading, error };
}
